/**
 * Client Onboarding
 * =================
 * New client setup, configuration, and go-live procedures.
 * 
 * Route: /wyldecrest/helpdesk/client-onboarding
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  UserPlus, 
  Settings, 
  FileText, 
  CheckCircle,
  Users,
  Rocket
} from 'lucide-react';

const sections = [
  { id: 'onboarding-overview', title: 'Onboarding Overview' },
  { id: 'pre-onboarding', title: 'Pre-Onboarding Requirements' },
  { id: 'system-setup', title: 'System Setup' },
  { id: 'contractor-setup', title: 'Contractor Network Setup' },
  { id: 'training-handover', title: 'Training & Handover' },
  { id: 'go-live', title: 'Go-Live Process' },
];

export default function ClientOnboardingPage() {
  return (
    <GovernancePageLayout
      title="Client Onboarding"
      subtitle="New client setup, configuration, and go-live procedures"
      icon={UserPlus}
      version="1.4"
      lastReviewed="2026-05-23"
      reviewedBy="Client Success Manager"
      sections={sections}
    >
      {/* Onboarding Overview */}
      <DocumentSection title="Onboarding Overview" icon={FileText} id="onboarding-overview">
        <p className="text-slate-300 mb-6">
          The client onboarding process ensures new clients are set up correctly and ready
          for a successful go-live. Standard onboarding takes 2-4 weeks depending on complexity.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Onboarding Phases</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6">
          {['Discovery', 'Configuration', 'Data Import', 'Training', 'Go-Live'].map((phase, idx) => (
            <div key={phase} className="flex items-center">
              <div className={`flex-1 p-3 rounded-lg text-center ${
                idx === 0 ? 'bg-emerald-900/30 border border-emerald-700/50' :
                idx === 4 ? 'bg-blue-900/30 border border-blue-700/50' :
                'bg-slate-800/50 border border-slate-700/50'
              }`}>
                <div className="text-xs text-slate-500 mb-1">Phase {idx + 1}</div>
                <div className={`font-medium ${
                  idx === 0 ? 'text-emerald-300' :
                  idx === 4 ? 'text-blue-300' :
                  'text-slate-300'
                }`}>{phase}</div>
              </div>
              {idx < 4 && <div className="text-slate-600 px-1">→</div>}
            </div>
          ))}
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Onboarding Team</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Role</th>
                <th className="pb-2">Responsibility</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 font-medium text-emerald-300">Client Success Manager</td>
                <td>Overall onboarding owner, client relationship</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-medium text-blue-300">Implementation Lead</td>
                <td>Technical setup, configuration, testing</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-medium text-purple-300">Training Specialist</td>
                <td>User training, documentation</td>
              </tr>
              <tr>
                <td className="py-2 font-medium text-amber-300">Operations Lead</td>
                <td>Contractor coordination, SLA setup</td>
              </tr>
            </tbody>
          </table>
        </div>
      </DocumentSection>

      {/* Pre-Onboarding */}
      <DocumentSection title="Pre-Onboarding Requirements" icon={CheckCircle} id="pre-onboarding">
        <p className="text-slate-300 mb-6">
          Before onboarding can begin, the following must be in place.
        </p>

        <Checklist 
          title="Pre-Onboarding Checklist"
          items={[
            'Contract signed and received',
            'Service scope document agreed',
            'Client primary contact identified',
            'Client technical contact identified',
            'Property/asset list provided',
            'Existing contractor information provided (if applicable)',
            'SLA requirements documented',
            'Billing terms confirmed',
            'Insurance certificates received',
            'Go-live target date agreed'
          ]}
        />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Required Documents from Client</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <h4 className="text-slate-300 font-semibold mb-2">Property Data</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Property addresses and postcodes</li>
              <li>• Property types and sizes</li>
              <li>• Access information</li>
              <li>• Asset inventory (if available)</li>
            </ul>
          </div>
          
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <h4 className="text-slate-300 font-semibold mb-2">Operational Data</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Existing maintenance contracts</li>
              <li>• Preferred contractors</li>
              <li>• Reporting requirements</li>
              <li>• Escalation contacts</li>
            </ul>
          </div>
        </div>
      </DocumentSection>

      {/* System Setup */}
      <DocumentSection title="System Setup" icon={Settings} id="system-setup">
        <p className="text-slate-300 mb-6">
          Technical configuration steps to set up the client in the platform.
        </p>

        <SOPStep 
          number={1}
          title="Create Client Record"
          description="Set up the client entity in the system."
          substeps={[
            'Create client profile with company details',
            'Configure billing information',
            'Set up client-specific settings',
            'Create client_id for data segregation'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Import Properties"
          description="Load property data into the system."
          substeps={[
            'Prepare property data in import template',
            'Validate data format and completeness',
            'Run import process',
            'Verify imported properties'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Configure SLAs"
          description="Set up client-specific SLA targets."
          substeps={[
            'Define priority levels and response times',
            'Configure SLA warning thresholds',
            'Set up auto-escalation rules',
            'Test SLA calculations'
          ]}
        />

        <SOPStep 
          number={4}
          title="Create User Accounts"
          description="Set up user access for client team."
          substeps={[
            'Create admin user account',
            'Set up helpdesk user accounts',
            'Configure role permissions',
            'Send welcome emails with credentials'
          ]}
        />

        <SOPStep 
          number={5}
          title="Configure Notifications"
          description="Set up email and portal notifications."
          substeps={[
            'Configure notification recipients',
            'Set up alert thresholds',
            'Enable relevant notification types',
            'Test notification delivery'
          ]}
        />

        <InfoBox title="Data Segregation">
          All client data is segregated by client_id. Staff users can only see data
          for clients they are assigned to. Super admins can access all clients.
        </InfoBox>
      </DocumentSection>

      {/* Contractor Setup */}
      <DocumentSection title="Contractor Network Setup" icon={Users} id="contractor-setup">
        <p className="text-slate-300 mb-6">
          Establish the contractor network to service the client's properties.
        </p>

        <SOPStep 
          number={1}
          title="Coverage Analysis"
          description="Map contractor coverage to client properties."
          substeps={[
            'Review client property locations',
            'Identify existing PHS contractors with coverage',
            'Identify coverage gaps requiring new contractors',
            'Prioritize contractor recruitment if needed'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Contractor Assignment"
          description="Link contractors to the client."
          substeps={[
            'Assign existing contractors to client',
            'Configure client-specific rates if applicable',
            'Set up contractor portal access for client jobs',
            'Notify contractors of new client'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Rate Configuration"
          description="Set up pricing for contractor work."
          substeps={[
            'Apply standard rate card or client-specific rates',
            'Configure trade-specific pricing',
            'Set up emergency rate multipliers',
            'Document any special pricing arrangements'
          ]}
        />

        <Checklist 
          title="Contractor Network Verification"
          items={[
            'All property postcodes have contractor coverage',
            'All required trades are covered',
            'Emergency response capability confirmed',
            'Contractor compliance documents current',
            'Portal access tested for each contractor',
            'Rates confirmed and documented'
          ]}
        />
      </DocumentSection>

      {/* Training & Handover */}
      <DocumentSection title="Training & Handover" icon={FileText} id="training-handover">
        <p className="text-slate-300 mb-6">
          Comprehensive training ensures the client team can effectively use the platform.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Training Program</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Session</th>
                <th className="pb-2">Audience</th>
                <th className="pb-2">Duration</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Platform Overview</td>
                <td>All users</td>
                <td>1 hour</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Job Management</td>
                <td>Helpdesk users</td>
                <td>2 hours</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Reporting & Analytics</td>
                <td>Managers</td>
                <td>1 hour</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Admin Functions</td>
                <td>Admin users</td>
                <td>1 hour</td>
              </tr>
              <tr>
                <td className="py-2">Portal Walkthrough</td>
                <td>Client portal users</td>
                <td>30 mins</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Handover Documentation</h3>
        
        <Checklist 
          title="Documentation Package"
          items={[
            'User guides provided and explained',
            'Quick reference cards for common tasks',
            'Escalation contacts documented',
            'SLA summary document delivered',
            'Contractor contact list provided',
            'Support process documented'
          ]}
        />
      </DocumentSection>

      {/* Go-Live */}
      <DocumentSection title="Go-Live Process" icon={Rocket} id="go-live">
        <p className="text-slate-300 mb-6">
          Controlled go-live process to ensure a smooth transition to live operations.
        </p>

        <SOPStep 
          number={1}
          title="Pre-Go-Live Review"
          description="Final verification before going live."
          substeps={[
            'All configuration verified and tested',
            'User accounts active and tested',
            'Contractor network ready',
            'Training completed',
            'Go-live checklist signed off'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Go-Live Day"
          description="Execute the go-live transition."
          substeps={[
            'Switch client status to "Live"',
            'Enable job creation',
            'Monitor first jobs closely',
            'Have support team on standby',
            'Conduct end-of-day review'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Hypercare Period"
          description="Intensive support for first 2 weeks."
          substeps={[
            'Daily check-ins with client',
            'Priority support queue',
            'Rapid issue resolution',
            'Gather feedback continuously',
            'Document any configuration adjustments'
          ]}
        />

        <WarningBox title="Hypercare Support">
          During the first 2 weeks post go-live, all client issues are treated as priority.
          Dedicated support resources should be allocated for rapid response.
        </WarningBox>

        <Checklist 
          title="Go-Live Day Checklist"
          items={[
            'All team members briefed on go-live',
            'Support escalation path confirmed',
            'Client contact available for queries',
            'Monitoring dashboards active',
            'First job created and verified',
            'Contractor response confirmed',
            'End-of-day summary sent to client'
          ]}
        />
      </DocumentSection>
    </GovernancePageLayout>
  );
}
